import React, { useEffect, useState } from "react";
import { useParams, Link as RouterLink } from "react-router-dom";
import {
  Box,
  Container,
  Typography,
  CircularProgress,
  Button,
  Chip,
} from "@mui/material";
import { getBlogPostBySlug } from "../../lib/sanity/data";
import type { Post } from "../../lib/sanity/types";
import { PortableText } from "next-sanity";
import formatDate from "../../helpers/formatDate";
import getImageUrl from "../../helpers/getImageUrl";
import SEO from "../seo/SEO";
import formatReadingTime from "../../helpers/formatReadingTime";
import SocialShare from "./SocialShare";

const BlogPost: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const [post, setPost] = useState<Post | null>(null);
  const [loading, setLoading] = useState(true);

  const fetchPost = async () => {
    if (!slug) return;

    try {
      setLoading(true);
      const postData = await getBlogPostBySlug(slug);
      setPost(postData);
    } catch (err) {
      console.error("Error fetching blog post:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPost();
  }, [slug]);

  if (loading) {
    return (
      <Box
        sx={{
          minHeight: "60vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (!post) {
    return (
      <Box sx={{ minHeight: "60vh", py: 6 }}>
        <Container maxWidth="md">
          <Typography variant="h5" color="#1e293b" gutterBottom>
            Article is not found.
          </Typography>
          <Button
            component={RouterLink}
            to="/blog"
            variant="contained"
            color="primary"
            sx={{ mt: 2 }}
          >
            Back to blog
          </Button>
        </Container>
      </Box>
    );
  }

  const seoTitle = post.seo?.title || post.title;
  const seoDescription = post.seo?.description;
  const seoImage = post.seo?.image?.asset?.url || getImageUrl(post);
  const seoKeywords = post.seo?.keywords;
  const postUrl = typeof window !== "undefined" ? window.location.href : "";

  return (
    <Box
      sx={{
        pb: { xs: 6, md: 10 },
        pt: {
          xs: 12,
          md: 10,
        },
      }}
    >
      <SEO
        title={seoTitle}
        description={seoDescription}
        image={seoImage}
        url={`/blog/${post.slug}`}
        keywords={seoKeywords}
      />
      <Container maxWidth="md">
        <Box
          sx={{
            position: "relative",
            display: "flex",
            gap: 4,
          }}
        >
          {/* Content */}
          <Box sx={{ flex: 1 }}>
            <Box
              sx={{
                mb: 4,
                borderRadius: 2,
                overflow: "hidden",
                display: "flex",
                justifyContent: "center",
              }}
            >
              <Box
                component="img"
                src={getImageUrl(post)}
                alt="Blog post"
                sx={{
                  objectFit: "cover",
                  display: "block",
                  width: "100%",
                }}
              />
            </Box>
            <Box
              sx={{
                mb: 2,
                display: "flex",
                flexWrap: "wrap",
                gap: 2,
                alignItems: "center",
                justifyContent: "space-between",
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  gap: 2,
                  flexWrap: "wrap",
                  alignItems: "center",
                }}
              >
                {post.author?.image?.asset?.url && (
                  <Box
                    component="img"
                    src={post.author.image.asset.url}
                    alt={post.author?.name || "Author"}
                    sx={{
                      width: 45,
                      height: 45,
                      borderRadius: "50%",
                      objectFit: "cover",
                    }}
                  />
                )}
                <Box sx={{ display: "flex", flexDirection: "column" }}>
                  <Typography
                    variant="body1"
                    sx={{ color: "#1e293b", fontWeight: 700 }}
                  >
                    {post.author?.name}
                  </Typography>
                  {post.publishedAt && (
                    <Typography variant="body2" sx={{ color: "#1e293b" }}>
                      {formatDate(post.publishedAt)}
                      {post.readingTime &&
                        ` | ${formatReadingTime(post.readingTime)} read`}
                    </Typography>
                  )}
                </Box>
              </Box>

              {post?.category?.slug && (
                <Chip
                  label={post.category?.title ?? "Category"}
                  component={RouterLink}
                  to={`/blog/category/${post?.category?.slug}`}
                  clickable
                  color="primary"
                  sx={{ textTransform: "capitalize" }}
                />
              )}
            </Box>

            <Typography
              variant="h3"
              sx={{
                mb: 3,
                color: "#1e293b",
                fontWeight: 700,
                fontSize: { xs: "36px", md: "48px" },
              }}
            >
              {post.title}
            </Typography>
            {post.excerpt && (
              <Box sx={{ mb: 4 }}>
                <PortableText value={post.excerpt} />
              </Box>
            )}
            <Box
              sx={{
                color: "#1e293b",
              }}
            >
              {post.body && post.body.length > 0 ? (
                <PortableText value={post.body} />
              ) : (
                <Typography variant="body1" sx={{ color: "#1e293b" }}>
                  No content.
                </Typography>
              )}
            </Box>
            {/* Mobile Share Section */}
            <SocialShare url={postUrl} title={post.title} variant="mobile" />
            <Box sx={{ mt: 4 }}>
              <Button
                component={RouterLink}
                to="/blog"
                variant="text"
                sx={{ color: "primary.main" }}
              >
                ← Back to blog
              </Button>
            </Box>
          </Box>
          {/* Desktop Share Section */}
          <SocialShare url={postUrl} title={post.title} variant="desktop" />
        </Box>
      </Container>
    </Box>
  );
};

export default BlogPost;
